import { Button, Dialog, Input, Label } from "@repo/ui";
import { useMutation } from "convex/react";
import { Loader2, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface CandidatRow {
  emailCandidat: string;
  nomCandidat: string;
  prenomCandidat: string;
  token: string;
}

interface AjouterCandidatsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  campagneId: Id<"campagnes">;
  campagneNom: string;
  anonyme?: boolean;
}

export function AjouterCandidatsDialog({
  isOpen,
  onClose,
  campagneId,
  campagneNom,
  anonyme = false,
}: AjouterCandidatsDialogProps) {
  const [candidats, setCandidats] = useState<CandidatRow[]>([
    { emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ajouterCandidats = useMutation(api.campagnes.ajouterCandidats);

  const reset = () => {
    setCandidats([{ emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" }]);
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const genererToken = (index: number) => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const part = (n: number) =>
      Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    const newToken = `${part(4)}-${part(4)}`;
    setCandidats((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, token: newToken };
      return updated;
    });
  };

  const addCandidatRow = () => {
    setCandidats((prev) => [
      ...prev,
      { emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" },
    ]);
  };

  const removeCandidatRow = (index: number) => {
    setCandidats((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCandidat = (index: number, field: keyof CandidatRow, value: string) => {
    setCandidats((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, [field]: value };
      return updated;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]!.split(",").map((h) => h.trim().toLowerCase());
      const nomIdx = header.findIndex((h) => ["nom", "nomcandidat"].includes(h));
      const prenomIdx = header.findIndex((h) => ["prenom", "prenomcandidat"].includes(h));
      const emailIdx = header.findIndex((h) => ["email", "emailcandidat"].includes(h));
      const tokenIdx = header.findIndex((h) => h === "token");
      if (nomIdx === -1) return;

      const parsed: CandidatRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i]!.split(",").map((c) => c.trim());
        const nomVal = cols[nomIdx] || "";
        const prenomVal = prenomIdx !== -1 ? (cols[prenomIdx] || "") : "";
        const emailVal = emailIdx !== -1 ? (cols[emailIdx] || "") : "";
        const tokenVal = tokenIdx !== -1 ? (cols[tokenIdx] || "") : "";
        if (nomVal) {
          parsed.push({
            nomCandidat: nomVal,
            prenomCandidat: prenomVal,
            emailCandidat: emailVal,
            token: tokenVal,
          });
        }
      }
      if (parsed.length > 0) {
        setCandidats((prev) => [
          ...prev.filter((c) => c.emailCandidat || c.nomCandidat || c.prenomCandidat || c.token),
          ...parsed,
        ]);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const validCandidats = candidats.filter((c) =>
        anonyme ? c.token.trim() : c.emailCandidat.trim() && c.nomCandidat.trim() && c.prenomCandidat.trim(),
      );

      if (validCandidats.length === 0) {
        throw new Error("Ajoutez au moins un candidat");
      }

      const result = await ajouterCandidats({
        campagneId,
        candidats: validCandidats.map((c) => ({
          emailCandidat: c.emailCandidat,
          nomCandidat: anonyme ? c.token : c.nomCandidat,
          prenomCandidat: c.prenomCandidat,
          token: c.token || undefined,
        })),
      });

      setSuccess(`${result.length} candidat(s) ajouté(s) à la campagne`);
      setCandidats([{ emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const nbValides = candidats.filter((c) =>
    anonyme ? c.token : c.emailCandidat && c.nomCandidat && c.prenomCandidat,
  ).length;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={`Ajouter des candidats — ${campagneNom}`}
      className="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="grid gap-4 p-1">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>
        )}
        {success && (
          <p className="text-sm text-green-600 bg-green-50 p-2 rounded">
            {success}
          </p>
        )}

        <div className="flex items-center justify-between">
          <Label>Candidats ({nbValides})</Label>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="add-csv-upload"
            />
            <label htmlFor="add-csv-upload">
              <Button type="button" variant="outline" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 mr-1" />
                  Import CSV
                </span>
              </Button>
            </label>
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto space-y-2 py-1 px-1 -mx-1">
          {candidats.map((c, index) => (
            <div key={index} className="flex items-end gap-2">
              {!anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <Label className="text-xs">Prénom</Label>}
                  <Input
                    value={c.prenomCandidat}
                    onChange={(e) => updateCandidat(index, "prenomCandidat", e.target.value)}
                    placeholder="Prénom"
                  />
                </div>
              )}
              {!anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <Label className="text-xs">Nom</Label>}
                  <Input
                    value={c.nomCandidat}
                    onChange={(e) => updateCandidat(index, "nomCandidat", e.target.value)}
                    placeholder="Nom"
                  />
                </div>
              )}
              {!anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <Label className="text-xs">Email</Label>}
                  <Input
                    type="email"
                    value={c.emailCandidat}
                    onChange={(e) => updateCandidat(index, "emailCandidat", e.target.value)}
                    placeholder="Email"
                  />
                </div>
              )}
              {anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <Label className="text-xs">Token</Label>}
                  <div className="flex gap-1">
                    <Input
                      value={c.token}
                      onChange={(e) => updateCandidat(index, "token", e.target.value)}
                      placeholder="abc-1234"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => genererToken(index)}>
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCandidatRow(index)}
                disabled={candidats.length <= 1}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addCandidatRow} className="w-full">
          <Plus className="h-4 w-4 mr-1" />
          Ajouter une ligne
        </Button>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Fermer
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ajout en cours...
              </>
            ) : (
              "Ajouter les candidats"
            )}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
